import { CustomMDXEditor, DateInputField, InputField, MultiSelectInputField, SelectInputField, UserAvatar } from "@/components/shared";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormLabel } from "@/components/ui/form";
import { ScrollArea } from "@/components/ui/scroll-area";
import { SelectItem } from "@/components/ui/select";
import { Task, TaskStatus, FetchUsersOnTenderQueryVariables, User, UserOnEntity, FetchUsersOnTenderDocument, FetchUsersOnTenderQuery, FetchLabelsDocument, FetchLabelsQuery, FetchLabelsQueryVariables } from "@/graphql/__generated__/graphql";
import { useCreateTaskMutationHook, useUpdateTaskMutationHook } from "@/hooks";
import { zodResolver } from "@hookform/resolvers/zod";
import { TFunction } from "i18next";
import { Dispatch, JSX, SetStateAction, useCallback } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { z } from "zod";
import { CircleLoader } from "../../loaders";
import { cn } from "@/lib/utils";
import { debounce } from "lodash";
import { useQuery } from "@apollo/client/react";

/**
 * Component to render an assignee item which consist of avatar and full name
 * @param assignee UserOnEntity object
 * @returns JSX.Element
 */
const AssigneeItem = ({ assignee }: { assignee: UserOnEntity }) => {
  return (
    <div key={assignee.id} className="flex flex-wrap items-center gap-2">
      <UserAvatar user={assignee} size="md" />
      <span className="text-sm">{`${assignee.firstname} ${assignee.lastname}`}</span>
      <span className="text-xs text-gray-400">{assignee.organization?.shortName}</span>
    </div>
  );
};

const createTaskSchema = (t: TFunction) => z.object({
  title: z.string({
    error: (issue) => issue.input === undefined ?
      `${t("Title")} ${t("schemas:form.isrequired")}`
      :
      `${t("Title")} ${t("schemas:form.bestring")}`,
  }).min(2, t("schemas:form.atleast2chars")).max(255, t("schemas:form.atmost255chars")),
  description: z.string().optional().or(z.literal("")),
  assignee: z.array(z.number()).optional(),
  deadline: z.date().optional(),
  labels: z.array(z.number()).optional(),
  status: z.enum([TaskStatus.Todo, TaskStatus.InProgress, TaskStatus.Done]),
});

interface TaskDialogContentProps {
  task?: Task;
  taskStatus?: TaskStatus;
  type: "user" | "tender";
  action: "create" | "update";
  canEdit?: boolean;
  onViewInTenderClick?: (_e: React.MouseEvent) => void;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}

/**
 * Reusable component to render the task dialog content, requires to be wrapped in a Dialog component with a DialogTrigger
 * @param task Task object
 * @param taskStatus Task status
 * @param type "user" or "tender" - used to determine the rendering of the component
 * @param action "create" or "update" - used to determine the action of the form when submitting
 * @param canEdit Boolean to determine if the user can edit the task
 * @param onViewInTenderClick Function to handle the view in tender click
 * @param setIsOpen Function to set the open state of the dialog
 * @returns {JSX.Element}
 */
function TaskDialogContent({ task, type, action, taskStatus, canEdit, onViewInTenderClick, setIsOpen }: TaskDialogContentProps): JSX.Element {
  const { t } = useTranslation(["components", "common"]);
  const { tenderID } = useParams<{ tenderID: string }>();

  // Mutation Hooks
  const { createTaskMutation, loading: createTaskLoading } = useCreateTaskMutationHook();
  const { updateTaskMutation, loading: updateTaskLoading } = useUpdateTaskMutationHook();

  const loading = createTaskLoading || updateTaskLoading;

  // Query Hooks
  const { data: fetchUsersOnTenderData } = useQuery<FetchUsersOnTenderQuery, FetchUsersOnTenderQueryVariables>(FetchUsersOnTenderDocument,{
    variables: {
      tenderId: Number(tenderID),
    },
    fetchPolicy: "cache-and-network",
    skip: tenderID === undefined,
  });
  const { data: fetchLabelsData } = useQuery<FetchLabelsQuery, FetchLabelsQueryVariables>(FetchLabelsDocument);

  const organizationUsers = fetchUsersOnTenderData?.fetchUsersOnTender || [];
  const labels = fetchLabelsData?.fetchLabels || [];

  const canEditAndInTender = type === "tender" && canEdit;

  // Form schema and default values
  const taskSchema = createTaskSchema(t);

  const taskSchemaDefaultValues = {
    title: task?.title ?? "",
    description: task?.description ?? "",
    assignee: task?.assignees.map((assignee: any) => assignee.id) ?? [],
    deadline: task?.deadline ? new Date(task?.deadline) : undefined,
    labels: task?.labels.map((label: any) => label.id) ?? [],
    status: task?.status ?? taskStatus,
  };

  const form = useForm<z.infer<typeof taskSchema>>({
    resolver: zodResolver(taskSchema),
    mode: "onChange",
    defaultValues: taskSchemaDefaultValues,
  });

  const onInvalid = (errors: unknown) => {
    console.error(errors);
  };

  // Watch form values to render UI in realtime
  const descriptionValue = form.watch("description");
  const selectedAssignees = form.watch("assignee");
  const selectedLabels = form.watch("labels");

  const debouncedSetDescription = useCallback(
    debounce((value: string) => {
      form.setValue("description", value);
    }, 150),
    [form]
  );

  const handleDescriptionChange = useCallback((value: string) => {
    debouncedSetDescription(value);
  }, [debouncedSetDescription]);

  // Creating label options for MultiSelectInputField
  const labelOptions = labels.map((label) => ({
    label: label.name,
    value: label.id,
  }));

  // Creating assignee options for MultiSelectInputField
  const assigneeOptions = organizationUsers.map((user) => ({
    label: `${user.firstname} ${user.lastname}`,
    value: user.id,
    prependLabelChildren: (
      <UserAvatar user={user as User} size="md" />
    ),
    appendLabelChildren: (
      <span className="text-xs text-gray-400">{user.organization?.shortName}</span>
    ),
  }));

  const onSubmit = async (data: any) => {
    // If action is create, call createTaskMutation to create a new task
    if (action === "create") {
      await createTaskMutation({
        variables: {
          input: {
            title: data.title,
            description: data.description,
            deadline: data.deadline,
            status: data.status,
            tenderId: Number(tenderID),
          },
          labelIds: data.labels,
          userIds: data.assignee,
        },
      });
    }

    // If action is update, call updateTaskMutation to update the task
    if (action === "update") {
      if (!task) {
        console.error("Task not found");
        return;
      }

      await updateTaskMutation({
        variables: {
          input: {
            id: task.id,
            title: data.title,
            description: data.description,
            deadline: data.deadline,
            status: data.status,
          },
          labelIds: data.labels,
          userIds: data.assignee,
        },
      });
    }

    setIsOpen(false);
  };

  const handleSubmitOnClick = form.handleSubmit(onSubmit, onInvalid);

  return (
    <DialogContent className="min-w-[70vw] max-w-[90vw] lg:max-w-[70vw] max-h-[90vh] overflow-y-auto overflow-x-clip h-full md:h-auto">
      <Form {...form}>
        <form className="w-[calc(90vw-3rem)] lg:w-[calc(70vw-3rem)]">
          <DialogHeader className="px-1 mb-4">
            <DialogTitle>{t("tasksboard.task.title")}</DialogTitle>
            <DialogDescription>{task?.tenderName}</DialogDescription>
          </DialogHeader>

          {/* Content wrapper */}
          <div className="flex gap-4 px-1 pb-4">
            {/* Left container */}
            <section className="flex-col-4 w-2/3">
              {/* Title */}
              <div>
                <InputField
                  label={t("tasksboard.task.inputs.title.label")}
                  name="title"
                  placeholder={t("tasksboard.task.inputs.title.placeholder")}
                  form={form}
                  className="w-full"
                  disabled={type === "user" || !canEdit}
                />
              </div>

              {/* Description */}
              <CustomMDXEditor
                value={descriptionValue || ""}
                onChange={handleDescriptionChange}
                placeholder={t("tasksboard.task.inputs.description.placeholder")}
                readOnly={type === "user" || !canEdit}
              />
            </section>

            {/* Right container */}
            <aside className="flex-col-4 pl-4 pb-4 border-l w-1/3">
              {
                action !== "create" && (
                  <div className="flex flex-col gap-2">
                    <FormLabel>{t("tasksboard.task.createdby")}</FormLabel>
                    <div className="flex gap-2 items-center">
                      <UserAvatar user={task?.createdBy} showTitle={true} size="md" />
                      <div className="text-xs">{task?.createdBy.firstname} {task?.createdBy.lastname}</div>
                    </div>
                  </div>
                )
              }

              {/* Assignees */}
              <div className="flex flex-col gap-2">
                {
                  canEditAndInTender ? (
                    <MultiSelectInputField
                      control={form.control}
                      name="assignee"
                      label={t("tasksboard.task.inputs.assignee.label")}
                      placeholder={t("tasksboard.task.inputs.assignee.placeholder")}
                      loading={false}
                      options={assigneeOptions}
                      className="w-full"
                    />
                  ) : (
                    <FormLabel>{t("tasksboard.task.inputs.assignee.label")}</FormLabel>
                  )
                }
                {/* Render selected assignee */}
                <ScrollArea className="overflow-y-auto max-h-[90px]">
                  <div className="flex flex-col gap-2">
                    {selectedAssignees?.map((assigneeId) => {
                      // Find the user in the organizationUsers array
                      const user = organizationUsers.find((u) => u.id === assigneeId);

                      // Safeguard if user not found (could happen if data changed or mismatch)
                      if (!user) return null;

                      return (
                        <AssigneeItem key={user.id} assignee={user as UserOnEntity} />
                      );
                    })}

                    {
                      tenderID === undefined && task?.assignees.map((assignee) => (
                        <AssigneeItem key={assignee.id} assignee={assignee} />
                      ))
                    }
                  </div>
                </ScrollArea>
              </div>

              {/* Labels */}
              <div className="flex flex-col gap-2">
                {
                  canEditAndInTender ? (
                    <MultiSelectInputField
                      control={form.control}
                      name="labels"
                      label={t("tasksboard.task.inputs.labels.label")}
                      placeholder={t("tasksboard.task.inputs.labels.placeholder")}
                      loading={false}
                      options={labelOptions}
                      className="w-full"
                    />
                  ) : (
                    <FormLabel>{t("tasksboard.task.inputs.labels.label")}</FormLabel>
                  )
                }
                {/* Render selected labels */}
                <div className="flex-wrap-2">
                  {
                    selectedLabels?.map((labelId) => {
                      const label = labels.find((l) => l.id === labelId);

                      if (!label) return null;

                      return (
                        <Badge key={labelId} variant="outline" className={cn(`bg-${label.color}-100`)}>{label.name}</Badge>
                      );
                    })
                  }
                </div>
              </div>

              {/* Deadline */}
              <DateInputField
                form={form}
                name="deadline"
                label={t("tasksboard.task.inputs.deadline.label")}
                placeholder={t("tasksboard.task.inputs.deadline.placeholder")}
                className="w-full"
                disabled={type === "user" || !canEdit}
              />

              {/* Status */}
              <SelectInputField
                form={form}
                name="status"
                label={t("tasksboard.task.inputs.status.label")}
                placeholder={t("tasksboard.task.inputs.status.placeholder")}
                className="w-full"
                disabled={type === "user" || !canEdit}
              >
                {
                  Object.values(TaskStatus).map((status) => (
                    <SelectItem key={status} value={status}>{t(`common:taskstatus.${status.toLowerCase()}`)}</SelectItem>
                  ))
                }
              </SelectInputField>
            </aside>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" type="button" disabled={loading}>{t("common:buttons.close")}</Button>
            </DialogClose>
            {
              canEditAndInTender ? (
                <Button
                  type="submit"
                  onClick={handleSubmitOnClick}
                  disabled={loading}
                  className="gap-2"
                >
                  <CircleLoader visible={loading} height={16} width={16} strokeWidth={2} />
                  {action === "create" ? t("common:buttons.create") : t("common:buttons.save")}
                </Button>
              ) : type === "user" ? (
                <Button type="button" onClick={onViewInTenderClick}>{t("tasksboard.viewinchance")}</Button>
              ) : null
            }
          </DialogFooter>
        </form>
      </Form>
    </DialogContent>
  );
}

export default TaskDialogContent;
